import React, { Component } from "react";
import * as THREE from "three";

import { Subscribe } from "unstated";
import StateContainer from "../../state/StateContainer";

import { withTranslation } from "react-i18next";
import pdfjsLib from "pdfjs-dist/lib/pdf";
import { saveAs } from "file-saver";
import deviceHelper from "../../utils/helpers/deviceHelper";

import sceneHelper from "../../utils/helpers/sceneHelper";
import ApartmentSidebar from "../../components/ApartmentSidebar/ApartmentSidebar";
import DisplayWrapper from "../../components/DisplayWrapper/DisplayWrapper";
import DefaultButton from "../../components/Buttons/DefaultButton/DefaultButton";
import DragControls from "../../utils/DragControls";
import BackButton from "../../components/Buttons/BackButton/BackButton";
import ToggleApartmentView from "../../components/ToggleApartmentView/ToggleApartmentView";

import "./ShowApartment.css";

class ShowApartment extends Component {
  _isMounted = false;

  state = {
    scene: "",
    camera: "",
    renderer: "",
    page: null,
    pdfScale: deviceHelper.isMobileDevice() ? 3 : 0.6,
    shownPdf: null,
    canvas: null,
    canvasScrollInterval: undefined,
    canvasWidth: 0,
    isMobile: deviceHelper.isMobileDevice(),
  };

  /**
   * Intializes apartment3DView scene.
   */
  async initializeApartmentScene() {
    let canvasWrapper = document.getElementById("apartmentCanvas__wrapper");

    let canvasWrapperProperties = canvasWrapper.getBoundingClientRect();

    let scene = new THREE.Scene();
    let camera = new THREE.PerspectiveCamera(
      65,
      canvasWrapperProperties.width / canvasWrapperProperties.height,
      0.1,
      1000
    );
    let canvas = document.getElementById("apartment-canvas");

    camera.position.z = 5;

    let renderer = new THREE.WebGLRenderer({
      antialias: true,
      canvas,
      alpha: true,
    });
    renderer.setSize(
      canvasWrapperProperties.width,
      canvasWrapperProperties.height
    );

    renderer.setPixelRatio(window.devicePixelRatio);

    let sprite = sceneHelper.initializeApartmentSprite();

    window.addEventListener(
      "resize",
      () => {
        this.onWindowResize(renderer, camera, canvas);
      },
      false
    );

    scene.add(sprite);

    await this.setState({
      scene: scene,
      camera: camera,
      renderer: renderer,
      frameId: 0,
    });

    const controls = new DragControls(camera, renderer);
    controls.setAllEvents();
  }

  /**
   * Handles resize event and adapts the apartment3DView canvas to the new viewport size.
   * @param renderer Three.js renderer for apartment3DView
   * @param camera Three.js camera for apartment3DView
   * @param canvas Canvas for apartment3DView
   */
  onWindowResize(renderer, camera) {
    let canvasWrapper = document.getElementById("apartmentCanvas__wrapper");

    if (!canvasWrapper) return;

    let canvasWrapperProperties = canvasWrapper.getBoundingClientRect();

    renderer.setSize(
      canvasWrapperProperties.width,
      canvasWrapperProperties.height
    );
    camera.aspect =
      canvasWrapperProperties.width / canvasWrapperProperties.height;
    camera.updateProjectionMatrix();
  }

  /**
   * Sets the frameId into state and starts the apartment3DView render loop.
   * @param renderer Three.js renderer for apartment3DView
   * @param camera Three.js camera for apartment3DView
   * @param canvas Canvas for apartment3DView
   */
  updateFrame(renderer, scene, camera) {
    this.setState({
      frameId: requestAnimationFrame(() => {
        this.updateFrame(renderer, scene, camera);
      }),
    });

    renderer.render(scene, camera);
  }

  /**
   * Initializes render loop.
   */
  initializeRenderLoop() {
    this.updateFrame(this.state.renderer, this.state.scene, this.state.camera);
  }

  /**
   * Dispatches an event that starts the rendering of the main scene.
   */

  /**
   * Handles the wheel event (zoom) for canvas apartment container PDF.
   */
  handlePDFScroll = async (e) => {
    e.preventDefault();
    if (this.state.canvasScrollInterval !== undefined) return;
    if (this.props.isApartment3DCanvasShown) return;

    let tempInterval = setTimeout(() => {
      let newScale = this.state.pdfScale;
      if (e.deltaY > 0) {
        if (newScale <= 0.5) {
          clearInterval(tempInterval);
          this.setState({ canvasScrollInterval: undefined });
          return;
        }
        newScale -= 0.2;
      } else if (e.deltaY < 0) {
        if (newScale >= 1.5999999999999999) {
          clearInterval(tempInterval);
          this.setState({ canvasScrollInterval: undefined });
          return;
        }
        newScale += 0.2;
      }
      this.setState({ pdfScale: newScale, canvasScrollInterval: undefined });
      this.displayPage(this.state.shownPdf, 1);

      clearInterval(tempInterval);
    }, 500);
    await this.setState({ canvasScrollInterval: tempInterval });
  };

  calculatePdfScale() {
    let difference = this.calculateDifferenceMultiplier(
      1920,
      window.innerWidth
    );

    return 1 + difference;
  }

  calculateDifferenceMultiplier(usualSize, currentSize) {
    return (currentSize - usualSize) / ((currentSize + usualSize) / 2);
  }

  /**
   * Sets the wheel event to the canvas and renders the PDF onto canvas.
   * @param page PDF page we want to display.
   */
  renderApartmentPDF(page) {
    this.setState({ page });
    page.cleanup();
   

    var viewport = page.getViewport({
      scale: this.state.pdfScale * this.calculatePdfScale(),
    });
    var canvas = document.getElementById("pdf-canvas");

    if (!canvas) return;

    var context = canvas.getContext("2d");
    const resolution = 2;

    canvas.height = resolution * viewport.height; //actual size
    canvas.width = resolution * viewport.width;

    canvas.style.height = viewport.height; //showing size will be smaller size
    canvas.style.width = viewport.width;

    this.setState({ canvasWidth: canvas.width });
    var renderContext = {
      canvasContext: context,
      viewport: viewport,
      transform: [resolution, 0, 0, resolution, 0, 0],
    };
    page.render(renderContext);
  }

  /**
   * Fetches the PDF page.
   * @param pdf PDF file from which we want to load the page.
   * @param num number of page we want to load.
   */
  displayPage(pdf, num) {
    pdf.getPage(num).then((page) => {
      this.renderApartmentPDF(page);
    });
  }

  /**
   * Fetches the PDF Document.
   * @param url Url of the pdf document we want to load.
   */
  loadPDFDocument(url) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.js`;
    var pdfDoc = pdfjsLib.getDocument(url);
    pdfDoc.promise.then((pdf) => {
      // if(this.state.isMobile)
      //   this.setState({ pdfScale: 1 });

      this.displayPage(pdf, 1);
      this.setState({ shownPdf: pdf });
    });
  }

  /**
   * Handles 3d image download
   * @param state2 state container
   */
  download3dImage(state2) {
    const imageUrl =
      state2.state.apartmentClickedInfo.highResUrls[
        state2.state.currentHotspotColumn
      ];

    saveAs(imageUrl, `${state2.state.apartmentClickedInfo.key}.jpg`);
  }

  /**
   * Handles pdf or 3d image download
   * @param state2 state container
   */
  download3DImageOrPdf(state2) {
    if (state2.state.isApartment3DCanvasShown)
      return (
        <div
          className="download-pdf"
          onClick={() => this.download3dImage(state2)}
        ></div>
      );

    return (
      // eslint-disable-next-line jsx-a11y/anchor-has-content
      <a
        href={state2.state.apartmentClickedInfo.twoDimensionalPlanUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="download-pdf"
        download
      />
    );
  }

  async componentDidMount() {
    await this.initializeApartmentScene();
    this.initializeRenderLoop();

    let fullscreenNode = document.getElementById("full-screenable-node");

    fullscreenNode.addEventListener("renderApartmentCanvas", (event) => {
      cancelAnimationFrame(this.state.frameId);
    });
    fullscreenNode.addEventListener("stopRenderingApartmentCanvas", (event) => {
      cancelAnimationFrame(this.state.frameId);
    });
    this.setState({ canvas: document.getElementById("pdf-canvas") });

    window.addEventListener("ApartmentInfoSet", (e) => {
      if (!this._isMounted) {
        return;
      }

      if (e.detail.clickedApartment !== undefined) {
        if (e.detail.clickedApartment.twoDimensionalPlanUrl !== "") {
          fullscreenNode.addEventListener("wheel", this.handlePDFScroll);

          this.loadPDFDocument(e.detail.clickedApartment.twoDimensionalPlanUrl);
        }
      }
    });

    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
    let fullscreenNode = document.getElementById("full-screenable-node");
    fullscreenNode.removeEventListener("wheel", this.handlePDFScroll);
  }

  render() {
    return (
      <Subscribe to={[StateContainer]}>
        {(state2) => (
          <div id="apartmentInfo" className="apartmentInfo-overlay">
            <ApartmentSidebar />

            <div className="apartmentInfo-container">
              <div id="apartmentCanvas__wrapper">
                <canvas
                  style={{
                    display: state2.state.isApartment3DCanvasShown
                      ? "block"
                      : "none",
                  }}
                  id="apartment-canvas"
                />
              </div>
              {!this.state.isMobile && (
                <div className="apartmentInfo-container-head">
                  <div className="apartmentInfo__button-container">
                    <div className="apartmentInfo__button-wrapper">
                      <BackButton frameId={this.state.frameId} />
                    </div>
                    {this.download3DImageOrPdf(state2)}
                  </div>
                </div>
              )}

              <div className="apartmentInfo-container-middle">
                {state2.state.isClickedOn3D ? null : state2.state
                    .apartmentClickedInfo !== undefined ? (
                  <div
                    id="pdf-container"
                    style={{
                      height: "100%",
                      width: "100%",
                      overflow: state2.state.isApartment3DCanvasShown
                        ? "hidden"
                        : "auto",
                      display:
                        state2.state.apartmentClickedInfo
                          .twoDimensionalPlanUrl === ""
                          ? "none"
                          : "relative",
                    }}
                  >
                    {
                      <canvas
                        style={{
                          opacity: state2.state.isApartment3DCanvasShown
                            ? "0"
                            : "1",
                        }}
                        id="pdf-canvas"
                      />
                    }
                  </div>
                ) : null}
              </div>

              <div className="apartmentInfo-container-bottom">
                {!this.state.isMobile ? (
                  <ToggleApartmentView />
                ) : null}
                

                <DisplayWrapper displayParameter="isApartmentFullscreenButtonShown">
                  <DefaultButton
                    buttonName="Apartment Fullscreen"
                    buttonClass="full_screen_button-apartment"
                  />
                </DisplayWrapper>
              </div>
            </div>
          </div>
        )}
      </Subscribe>
    );
  }
}

export default withTranslation()(ShowApartment);
